'use client';
import { jsxs, jsx } from 'react/jsx-runtime';
import { twJoin } from 'tailwind-merge';
import { forwardRef, useState, useEffect } from 'react';
import SystemIcon from '../../DataDisplays/SystemIcon/SystemIcon.js';
import Typography from '../../Base/Typography/Typography.js';
import Box from '../../Base/Layouts/Box/Box.js';
import '../../Base/Layouts/FullBleed/FullBleed.js';
import { generateBackgroundColor, generateBackgroundPadding, generateLeadingIconColor, generateLabelStyle, generateTrailingIconStyle } from './ChoiceChip.styles.js';

function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
/**
 * 선택 가능한 칩 컴포넌트입니다.
 *
 * @component
 * @param {Object} props - 컴포넌트 속성
 * @param {string} props.label - 칩에 표시될 텍스트 라벨
 * @param {boolean} [props.checked=false] - 체크박스의 선택 상태를 제어
 * @param {boolean} [props.error=false] - 에러 상태를 표시
 * @param {LargeSystemIconName} [props.leadingIcon] - 칩 좌측에 표시되는 큰 아이콘
 * @param {MediumSystemIconName} [props.trailingIcon] - 칩 우측에 표시되는 중간 크기 아이콘
 * @param {function} [props.onChange] - 체크박스 상태 변경 시 호출되는 이벤트 핸들러
 *
 * @example
 * // 기본 사용
 * <ChoiceChip
 *   label="선택 옵션"
 * />
 *
 * // 아이콘이 포함된 칩
 * <ChoiceChip
 *   label="아이콘 옵션"
 *   leadingIcon="home"
 *   trailingIcon="close"
 * />
 *
 * // 에러 상태의 칩
 * <ChoiceChip
 *   label="에러 상태"
 *   error={true}
 * />
 *
 * // 선택된 상태의 칩
 * <ChoiceChip
 *   label="선택된 옵션"
 *   checked={true}
 *   onChange={(e) => console.log('선택 상태:', e.target.checked)}
 * />
 */ const ChoiceChip = /*#__PURE__*/ forwardRef((_param, ref)=>{
    var { label, checked = false, error = false, leadingIcon, trailingIcon, onChange } = _param, rest = _object_without_properties(_param, [
        "label",
        "checked",
        "error",
        "leadingIcon",
        "trailingIcon",
        "onChange"
    ]);
    const [isChecked, setIsChecked] = useState(checked);
    useEffect(()=>{
        setIsChecked(checked);
    }, [
        checked
    ]);
    const handleChange = (e)=>{
        if (onChange) {
            onChange(e);
            setIsChecked(e.target.checked);
        }
    };
    return /*#__PURE__*/ jsxs("label", {
        children: [
            /*#__PURE__*/ jsx("input", _object_spread_props(_object_spread({
                type: "checkbox",
                checked: isChecked,
                className: "absolute h-0 w-0 opacity-0",
                onChange: handleChange
            }, rest), {
                ref: ref
            })),
            /*#__PURE__*/ jsxs("div", {
                "data-testid": "design-system-choiceChip",
                className: twJoin('flex w-fit cursor-pointer select-none items-center gap-2 rounded-full', generateBackgroundColor(isChecked, error), generateBackgroundPadding(!!leadingIcon)),
                children: [
                    leadingIcon && /*#__PURE__*/ jsx(Box, {
                        className: "flex size-8 items-center justify-center rounded-full bg-w-white p-0.5",
                        "data-testid": "design-system-choiceChip--leading-icon-container",
                        children: /*#__PURE__*/ jsx(SystemIcon, {
                            name: leadingIcon,
                            className: generateLeadingIconColor(isChecked, error)
                        })
                    }),
                    /*#__PURE__*/ jsxs(Box, {
                        className: "flex items-center gap-1.5",
                        children: [
                            /*#__PURE__*/ jsx(Typography, {
                                "data-testid": "design-system-choiceChip-label",
                                variant: "body1",
                                className: generateLabelStyle(isChecked, error),
                                children: label
                            }),
                            trailingIcon && /*#__PURE__*/ jsx(SystemIcon, {
                                name: trailingIcon,
                                className: generateTrailingIconStyle(isChecked, error)
                            })
                        ]
                    })
                ]
            })
        ]
    });
});
ChoiceChip.displayName = 'ChoiceChip';

export { ChoiceChip as default };
